/**
 * relationEventsSlice — Spawning des événements narratifs Diplomatie.
 *
 * Évalue chaque jour les templates (`data/relationEventTemplates.js`) :
 *   - filtre les concurrents éligibles (condition + cooldown OK)
 *   - tire au sort avec la probabilité du template
 *   - instancie le message via `pushCustomMessage`
 *
 * État :
 *   - relationEventCooldowns : { `${templateId}__${competitorId}`: dayLastFired }
 *
 * Branchement : `relationEventsTick()` appelé depuis `coreSlice.nextDay`.
 *
 * 17/06/2026 — Création (Lot 6 Phase 3).
 */

import { COMPETITORS } from '../../data/competitors';
import { RELATION_EVENT_TEMPLATES } from '../../data/relationEventTemplates';

/** Limite à 1 spawn / jour pour éviter la noyade de la boîte. */
const MAX_SPAWN_PER_TICK = 1;

export const createRelationEventsSlice = (set, get) => ({
  /**
   * Cooldown par couple (template, concurrent). Persiste la date du
   * dernier spawn pour empêcher la répétition (ex: OPA TransNord pas
   * deux fois dans la même année).
   */
  relationEventCooldowns: {},

  /**
   * Appelé chaque jour depuis nextDay. Évalue les templates, tire et
   * spawn éventuellement un événement narratif.
   *
   * @returns {{ spawned: number, evaluated: number }}
   */
  relationEventsTick: () => {
    const state = get();
    if (typeof get().pushCustomMessage !== 'function') return { spawned: 0, evaluated: 0 };
    const today = state.totalDays || 0;
    const cooldowns = state.relationEventCooldowns || {};
    let spawned = 0;
    let evaluated = 0;

    // On itère tous les couples (template, concurrent), on filtre, on tire.
    // L'ordre est : templates dans l'ordre de RELATION_EVENT_TEMPLATES
    // (cf. priorité de pertinence : OPA > fusion > plainte > etc.),
    // concurrents dans l'ordre de COMPETITORS.
    const candidates = [];
    for (const tpl of RELATION_EVENT_TEMPLATES) {
      for (const comp of COMPETITORS) {
        evaluated += 1;
        const cdKey = `${tpl.id}__${comp.id}`;
        const lastFired = cooldowns[cdKey] || 0;
        // 17/06/2026 — Cooldown ne bloque QUE si déjà tiré au moins une fois
        if (lastFired > 0 && today - lastFired < tpl.cooldownDays) continue;
        // Condition métier
        try {
          if (!tpl.condition(state, comp)) continue;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[relationEvents] condition failed', tpl.id, comp.id, err);
          continue;
        }
        // Probabilité quotidienne
        if (Math.random() > tpl.probability) continue;
        candidates.push({ tpl, comp });
      }
    }

    // On prend au plus MAX_SPAWN_PER_TICK candidats — le 1er trouvé est
    // priorisé (les templates importants sont en début de liste).
    const toSpawn = candidates.slice(0, MAX_SPAWN_PER_TICK);
    const newCooldowns = { ...cooldowns };

    toSpawn.forEach(({ tpl, comp }) => {
      try {
        const msgPayload = tpl.build(state, comp);
        // On enrichit le payload avec le template id pour les tests + analytics
        const enriched = {
          ...msgPayload,
          templateId: tpl.id,
          payload: { ...(msgPayload.payload || {}), templateId: tpl.id, competitorId: comp.id, eventType: tpl.type },
        };
        get().pushCustomMessage(enriched);
        newCooldowns[`${tpl.id}__${comp.id}`] = today;
        spawned += 1;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug(`[relationEvents] spawned ${tpl.id} (${comp.id}) on day ${today}`);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[relationEvents] build failed', tpl.id, comp.id, err);
      }
    });

    if (spawned > 0) {
      set({ relationEventCooldowns: newCooldowns });
    }
    return { spawned, evaluated };
  },
});
